import type { Composer } from 'vue-i18n';

declare global {
  interface Window {
    Cookiebot?: unknown;
    CookieConsent?: unknown;
    _cookiebotInitialized?: boolean;
  }
}

const SCRIPT_SELECTOR = 'script#Cookiebot, script[src*="cookiebot.com/uc.js"], script[src*="cookiebot.eu/uc.js"]';
const RENDERED_ELEMENTS_SELECTOR = '[id^="CybotCookiebot"], #CookiebotWidget';

/**
 * Cookiebot renders its banner once, in the culture detected when its script loads.
 * Switching locale client-side therefore needs the script re-injected with the new culture.
 */
const reloadCookiebotWithLanguage = (language: string) => {
  const script = document.querySelector<HTMLScriptElement>(SCRIPT_SELECTOR);

  if (!script) {
    return;
  }

  const culture = language.toUpperCase();
  const scriptUrl = new URL(script.src);
  const currentCulture = scriptUrl.searchParams.get('culture') || script.getAttribute('data-culture');
  if (currentCulture?.toUpperCase() === culture) {
    return;
  }

  const reloadedScript = document.createElement('script');
  Array.from(script.attributes).forEach(({ name, value }) => reloadedScript.setAttribute(name, value));
  reloadedScript.setAttribute('data-culture', culture);
  scriptUrl.searchParams.set('culture', culture);
  reloadedScript.src = scriptUrl.href;

  script.remove();
  document.querySelectorAll(RENDERED_ELEMENTS_SELECTOR).forEach((element) => element.remove());
  delete window.Cookiebot;
  delete window.CookieConsent;

  document.head.appendChild(reloadedScript);
};

export default defineNuxtPlugin((nuxtApp) => {
  if (window._cookiebotInitialized) {
    return;
  }

  window._cookiebotInitialized = true;
  const controller = new AbortController();
  const { signal } = controller;

  const { locale } = nuxtApp.$i18n as Composer;
  let cookiebotReady = false;
  const synchronizeLanguage = () => {
    // Finish Cookiebot's initialization before replacing its script and globals.
    queueMicrotask(() => {
      if (!signal.aborted && (cookiebotReady || document.querySelector(RENDERED_ELEMENTS_SELECTOR))) {
        reloadCookiebotWithLanguage(locale.value);
      }
    });
  };
  const onCookiebotReady = () => {
    cookiebotReady = true;
    synchronizeLanguage();
  };
  window.addEventListener('CookiebotOnDialogInit', onCookiebotReady, { signal });
  window.addEventListener('CookiebotOnConsentReady', onCookiebotReady, { signal });
  const stopLocaleWatcher = watch(locale, synchronizeLanguage, { immediate: true });

  window.addEventListener('CookiebotOnAccept', () => {
    const cookieConsent = (window as any).CookieConsent;

    if (cookieConsent?.consent?.marketing && cookieConsent?.consent?.statistics) {
      nuxtApp.callHook('cookiebot:accepted');
    }
    nuxtApp.callHook('cookiebot:consent-changed', {
      marketing: cookieConsent?.consent?.marketing || false,
      statistics: cookieConsent?.consent?.statistics || false,
    });
  }, { signal });

  window.addEventListener('CookiebotOnDecline', () => {
    nuxtApp.callHook('cookiebot:declined');
    nuxtApp.callHook('cookiebot:consent-changed', {
      marketing: false,
      statistics: false,
    });
  }, { signal });

  const cleanup = () => {
    if (signal.aborted) return;
    controller.abort();
    stopLocaleWatcher();
    delete window._cookiebotInitialized;
  };
  nuxtApp.vueApp.onUnmount(cleanup);
  import.meta.hot?.dispose(cleanup);
});
